import React, { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { Link } from 'react-router-dom';
import {
    FaEnvelope,
    FaLock,
    FaEye,
    FaEyeSlash,
    FaPencilAlt,
    FaArrowRight,
    FaRocket,
    FaChartLine,
    FaCheck,
    FaBuilding,
    FaUser,
    FaComments,
    FaExclamationCircle,
} from 'react-icons/fa';

import Modal from 'shared/ui/Modal';
import { notifySignupLead, register } from 'services/signupService';
import { updateUserType } from 'services/userTypeService';
import './Login.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TOTAL_STEPS = 3;

const USER_TYPE_OPTIONS = [
    { value: 'agence', label: 'Agence', icon: <FaBuilding /> },
    { value: 'model', label: 'Modèle', icon: <FaUser /> },
    { value: 'chatter', label: 'Chatter', icon: <FaComments /> },
];

const RegisterFlow = ({ onLogin, onSwitchToLogin, onClose }) => {
    const [step, setStep] = useState(0);
    const [mail, setMail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [userType, setUserType] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [hasUnlockedConfirm, setHasUnlockedConfirm] = useState(false);
    const [showAbandonWarning, setShowAbandonWarning] = useState(false);
    const guardActive = useRef(false);
    const leadSentRef = useRef(false);

    // Reveal the confirmation field only once the password reaches the minimum
    // length — keeps the password step light until it's actually needed.
    useEffect(() => {
        if (password.length >= 6) {
            setHasUnlockedConfirm(true);
        }
    }, [password]);

    // Guard against accidental abandonment ONLY on the password step (step 1) —
    // the high-stakes moment where an email is invested but the account does not
    // exist yet. From step 2 on, the account is already created (recoverable via
    // re-engagement + the app's UserTypeModal), so we let the user leave freely.
    // Browser back → confirmation modal; tab close / refresh → native prompt. The
    // in-app "edit email" pencil bypasses this since it just calls setStep(0)
    // without triggering popstate or beforeunload.
    useEffect(() => {
        if (step !== 1) {
            guardActive.current = false;
            return undefined;
        }
        guardActive.current = true;
        window.history.pushState({ unbotSignupGuard: true }, '');

        const onPopState = () => {
            if (!guardActive.current) return;
            setShowAbandonWarning(true);
            // Re-arm the trap so consecutive back presses keep showing the modal.
            window.history.pushState({ unbotSignupGuard: true }, '');
        };

        const onBeforeUnload = (e) => {
            if (!guardActive.current) return;
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('popstate', onPopState);
        window.addEventListener('beforeunload', onBeforeUnload);

        return () => {
            window.removeEventListener('popstate', onPopState);
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, [step]);

    const goToStep = (nextStep) => {
        setError('');
        setStep(nextStep);
    };

    const handleEmailSubmit = (e) => {
        e.preventDefault();
        if (!EMAIL_REGEX.test(mail)) {
            setError('Entre une adresse email valide.');
            return;
        }
        // Fire-and-forget Discord lead ping — once per session, never blocks.
        if (!leadSentRef.current) {
            leadSentRef.current = true;
            notifySignupLead(mail);
        }
        goToStep(1);
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            setError('Ton mot de passe doit contenir au moins 6 caractères.');
            return;
        }
        if (password !== confirmation) {
            setError('Le mot de passe et la confirmation sont différents.');
            return;
        }

        setError('');
        setLoading(true);

        try {
            // Create the account now (email + password only). It persists even if
            // the user drops off before choosing a profile type.
            const data = await register({ mail, password });

            if (data && data.error) {
                setError(data.error.message);
                setLoading(false);
                return;
            }

            if (!data || !data.jwt) {
                setError("Une erreur est survenue. Réessaie dans un instant.");
                setLoading(false);
                return;
            }

            Cookies.set('user_token', data.jwt, { expires: 365 });
            setLoading(false);
            // Account exists from here on — disarm before leaving the guarded step.
            guardActive.current = false;
            goToStep(2);
        } catch (err) {
            const apiMessage = err?.response?.data?.error?.message;
            setError(apiMessage || "Une erreur est survenue. Réessaie dans un instant.");
            setLoading(false);
        }
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        if (!userType) {
            setError('Sélectionne ton profil pour continuer.');
            return;
        }

        setError('');
        setLoading(true);

        try {
            // Account already created at the password step — just attach the
            // chosen profile type. updateUserType refreshes the stored token.
            await updateUserType(userType);
            guardActive.current = false;
            onLogin();
        } catch (err) {
            setError(err?.message || "Une erreur est survenue. Réessaie dans un instant.");
            setLoading(false);
        }
    };

    const handleEditEmail = () => {
        // Intentional in-app navigation back to the email step — does not arm
        // the abandon guard.
        goToStep(0);
    };

    const handleStayInSignup = () => {
        setShowAbandonWarning(false);
    };

    const handleConfirmAbandon = () => {
        guardActive.current = false;
        setShowAbandonWarning(false);
        if (onClose) {
            onClose();
        }
    };

    const renderError = () =>
        error && (
            <div className="auth-error" role="alert">
                <FaExclamationCircle />
                <span>{error}</span>
            </div>
        );

    return (
        <>
            <div className="auth-progress" aria-hidden="true">
                {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
                    <div
                        key={index}
                        className={`auth-progress-segment ${index < step ? 'is-done' : ''} ${index === step ? 'is-active' : ''}`}
                    />
                ))}
            </div>

            {/* Étape 0 — Email */}
            {step === 0 && (
                <form key="step-email" className="auth-step" onSubmit={handleEmailSubmit}>
                    <h1 className="login-title">Crée ton compte</h1>
                    <p className="login-subtitle">Commençons par ton email.</p>

                    {renderError()}

                    <label className="auth-label" htmlFor="signup-email">Ton email</label>
                    <div className="auth-field">
                        <span className="auth-field-icon"><FaEnvelope /></span>
                        <input
                            id="signup-email"
                            type="email"
                            className="auth-input"
                            placeholder="exemple@email.com"
                            value={mail}
                            onChange={(e) => { setMail(e.target.value); setError(''); }}
                            autoFocus
                            required
                        />
                    </div>

                    <button type="submit" className="next-button">
                        Continuer
                        <FaArrowRight />
                    </button>

                    <p className="legal-disclaimer">
                        En t'inscrivant, tu acceptes nos{' '}
                        <Link to="/cgu" className="legal-link">Conditions Générales d’Utilisation</Link>.
                    </p>

                    <p className="account-text" onClick={onSwitchToLogin}>
                        J'ai déjà un compte <span className="auth-link">Me connecter</span>
                    </p>
                </form>
            )}

            {/* Étape 1 — Mot de passe */}
            {step === 1 && (
                <form key="step-password" className="auth-step" onSubmit={handlePasswordSubmit}>
                    <h1 className="login-title">Crée ton compte</h1>

                    <div className="auth-banner">
                        <div className="auth-banner-shimmer" aria-hidden="true" />
                        <div className="auth-banner-content">
                            <div className="auth-banner-icon"><FaRocket /></div>
                            <div>
                                <p className="auth-banner-title">Plus qu'une étape !</p>
                                <p className="auth-banner-text">
                                    Des milliers de créateurs automatisent déjà leurs conversations avec Unbot.
                                </p>
                                <span className="auth-banner-pill">
                                    <FaChartLine />
                                    Rejoins-les
                                </span>
                            </div>
                        </div>
                    </div>

                    {renderError()}

                    <div className="auth-email-pill">
                        <FaEnvelope />
                        <span className="auth-email-text">{mail}</span>
                        <button
                            type="button"
                            className="auth-email-pill-edit"
                            onClick={handleEditEmail}
                            aria-label="Modifier l'email"
                            title="Modifier l'email"
                        >
                            <FaPencilAlt />
                        </button>
                    </div>

                    <label className="auth-label" htmlFor="signup-password">Crée ton mot de passe</label>
                    <div className="auth-field">
                        <span className="auth-field-icon"><FaLock /></span>
                        <input
                            id="signup-password"
                            type={showPassword ? 'text' : 'password'}
                            className="auth-input has-toggle"
                            placeholder="6 caractères minimum"
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); setError(''); }}
                            autoFocus
                            required
                            minLength="6"
                        />
                        <button
                            type="button"
                            className="auth-toggle"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                    </div>

                    {hasUnlockedConfirm && (
                        <div className="auth-slide-up">
                            <label className="auth-label" htmlFor="signup-confirm">Confirme ton mot de passe</label>
                            <div className="auth-field">
                                <span className="auth-field-icon"><FaLock /></span>
                                <input
                                    id="signup-confirm"
                                    type={showConfirm ? 'text' : 'password'}
                                    className="auth-input has-toggle"
                                    placeholder="Saisis-le à nouveau"
                                    value={confirmation}
                                    onChange={(e) => { setConfirmation(e.target.value); setError(''); }}
                                    required
                                />
                                <button
                                    type="button"
                                    className="auth-toggle"
                                    onClick={() => setShowConfirm((v) => !v)}
                                    aria-label={showConfirm ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                >
                                    {showConfirm ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>
                    )}

                    <button type="submit" className="next-button" disabled={loading}>
                        {loading ? 'Création de ton compte…' : (
                            <>
                                Continuer
                                <FaArrowRight />
                            </>
                        )}
                    </button>
                </form>
            )}

            {/* Étape 2 — Profil */}
            {step === 2 && (
                <form key="step-profile" className="auth-step" onSubmit={handleProfileSubmit}>
                    <h1 className="login-title">Tu es ?</h1>
                    <p className="login-subtitle">Pour personnaliser ton expérience Unbot.</p>

                    {renderError()}

                    <div className="user-type-options">
                        {USER_TYPE_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                className={`user-type-option ${userType === option.value ? 'selected' : ''}`}
                                onClick={() => { setUserType(option.value); setError(''); }}
                            >
                                <span className="user-type-option-icon">{option.icon}</span>
                                {option.label}
                            </button>
                        ))}
                    </div>

                    <button type="submit" className="next-button" disabled={loading} style={{ marginTop: 22 }}>
                        {loading ? 'Finalisation…' : (
                            <>
                                <FaCheck />
                                Accéder à Unbot
                            </>
                        )}
                    </button>
                </form>
            )}

            <Modal
                isOpen={showAbandonWarning}
                onClose={handleStayInSignup}
                size="small"
                closeLabel="Rester"
            >
                <div className="auth-abandon">
                    <div className="auth-abandon-icon"><FaRocket /></div>
                    <h3 className="auth-abandon-title">Attends, tu y es presque !</h3>
                    <p className="auth-abandon-text">
                        Ton compte est à quelques secondes. Termine ton inscription et laisse Unbot
                        gérer tes conversations pendant que tu te concentres sur l'essentiel.
                    </p>
                    <div className="auth-abandon-avatars">
                        <div className="auth-avatar-stack">
                            <span className="auth-avatar auth-avatar-1" />
                            <span className="auth-avatar auth-avatar-2" />
                            <span className="auth-avatar auth-avatar-3" />
                            <span className="auth-avatar auth-avatar-4" />
                        </div>
                        <span className="auth-avatar-caption">Des milliers de créateurs nous font déjà confiance</span>
                    </div>
                    <div className="auth-abandon-actions">
                        <button type="button" className="next-button" onClick={handleStayInSignup}>
                            <FaRocket />
                            Je continue mon inscription
                        </button>
                        <button type="button" className="auth-secondary-button" onClick={handleConfirmAbandon}>
                            Quitter quand même
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default RegisterFlow;
